// All KleverBook API calls now target the D2P / C5M backend so the standalone
// app shares users and books with the d2p website. The base URL is read from
// the build-time env var REACT_APP_D2P_BASE_URL — set it in `.env.development`
// and `.env.production` (or pass at build time for iOS/Android Capacitor builds).

// If REACT_APP_D2P_BASE_URL isn't set, default to a local d2p on port 3001.
// This used to default to :3000 (the kleverbook origin itself), which silently
// caused 404s on every API call when env loading failed.
// Default to the live D2P backend. Device builds (Android/iOS) can't reach a
// localhost dev server, so production must never fall back to localhost.
const FALLBACK_BASE_URL = 'https://c5m.world/';

// IMPORTANT: read process.env.REACT_APP_D2P_BASE_URL with plain dot access.
// Create-React-App's build-time substitution (webpack DefinePlugin) only
// replaces the exact literal `process.env.REACT_APP_D2P_BASE_URL`. Using
// optional chaining (`process.env?.REACT_APP_...`) defeats the static
// replacement, leaving `process.env` undefined in the webview at runtime —
// which silently fell back to localhost and broke every API call on device.
const envBaseUrl = process.env.REACT_APP_D2P_BASE_URL;
const rawBaseUrl = envBaseUrl && envBaseUrl.trim() ? envBaseUrl : FALLBACK_BASE_URL;

// Ensure trailing slash so endpoint concatenation is consistent
export const BASE_URL = rawBaseUrl.endsWith('/') ? rawBaseUrl : `${rawBaseUrl}/`;

export const API = {
  LOGIN: 'api/d2p/auth/login',
  REGISTER: 'api/d2p/auth/create-user',
  FORGOT_PASSWORD: 'api/d2p/auth/forgot-password',
  AUTH_STATUS: 'api/d2p/auth/status',

  // User profile (GET + PATCH). PATCH accepts application/json (name +
  // countryCode) OR multipart/form-data (with an `avatar` File field).
  // Auth: Bearer token, same as the other D2P routes.
  USER_PROFILE: 'api/d2p/user/user-profile',

  // Notifications (GET list + unread count, PATCH mark-read, DELETE).
  // Bearer auth; scope is D2P/KleverBook types only.
  NOTIFICATIONS: 'api/d2p/user/notifications',

  // Unified Discover search. mode = 'books' | 'authors' | 'genres'.
  // Returns paginated results with covers, ratings, and bucket counts.
  // Empty `q` returns a default browseable payload per mode.
  SEARCH: 'api/d2p/kleverbook/search',

  // "Popular this week/month/all" ranking for the Discover landing.
  // Score = averageRating * log10(reviewCount + 1), gated by recent
  // download activity in the window.
  POPULAR: 'api/d2p/kleverbook/popular',

  // KleverBook books
  ADVERTISED_BOOKS: 'api/d2p/kleverbook/books',
  FEATURED_BOOKS: 'api/d2p/kleverbook/featured',
  BOOK_DETAIL: (id: string) => `api/d2p/kleverbook/books/${id}`,

  // Owned books (D2P UserLibrary purchases) + in-app checkout
  OWNED_LIBRARY: 'api/d2p/kleverbook/library',
  CHECKOUT_DIGITAL: 'api/d2p/kleverbook/checkout/digital',

  // Server-streamed PDF endpoints (auth via ?token query param so pdf.js can
  // load them without setting Authorization headers from its worker).
  DOWNLOAD_FILE: (downloadId: string) =>
    `api/d2p/kleverbook/downloads/${downloadId}/file`,
  OWNED_FILE: (libraryEntryId: string) =>
    `api/d2p/kleverbook/library/${libraryEntryId}/file`,

  WISHLIST: 'api/d2p/user/wishlist',
  WISHLIST_REMOVE: (bookId: string) => `api/d2p/user/wishlist/${bookId}`,

  DOWNLOAD_BOOK: 'api/d2p/kleverbook/downloads',
  DOWNLOADED_BOOKS: 'api/d2p/kleverbook/downloads',
  UPDATE_PROGRESS: (downloadId: string) =>
    `api/d2p/kleverbook/downloads/${downloadId}/progress`,
  AD_TRACK: (downloadId: string) =>
    `api/d2p/kleverbook/downloads/${downloadId}/ad-view`,

  DEFAULT_ADS: 'api/d2p/kleverbook/ads/default',

  ADS_SERVE: 'api/d2p/kleverbook/ads/serve',
  SLIDER_IMAGES: 'api/d2p/kleverbook/slider',


  CHECKOUT_AD_FREE: 'api/d2p/kleverbook/checkout/ad-free',

  AD_CLICK: (adId: string) => `api/d2p/kleverbook/ads/click/${adId}`,
};
